import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { templatesApi, type Template, type VariableType } from "../api/templates";
import { providersApi, type ProviderConfig } from "../api/providers";
import { sendApi, type SendResult } from "../api/send";
import { ApiError } from "../api/client";

type Mode = "template" | "raw";

interface SendError {
  title: string;
  hint: string;
  detail: string;
  messageId?: string;
  logsUrl?: string;
}

function explainError(err: ApiError): SendError {
  const detail =
    err.details && typeof err.details === "object"
      ? JSON.stringify(err.details)
      : err.message;
  switch (err.code) {
    case "unknown_from_domain":
      return {
        title: "From domain not configured",
        hint: "Add this domain on the Providers page (and verify it with Brevo or Resend first).",
        detail,
      };
    case "template_not_found":
      return {
        title: "Template not found",
        hint: "It may have been deleted, or the templateId is wrong.",
        detail,
      };
    case "template_render_failed":
      return {
        title: "Template variables don't match the schema",
        hint: "Check that every variable defined on the template is provided with the right type.",
        detail,
      };
    case "provider_failed":
      return {
        title: "Email provider rejected the request",
        hint:
          "Most often this means the provider API key in SSM Parameter Store is invalid or missing. " +
          "Update /imera/brevo/api-key or /imera/resend/api-key as a SecureString and redeploy the backend " +
          "(or wait for the warm Lambda to recycle).",
        detail,
        messageId: err.messageId,
        logsUrl: err.logsUrl,
      };
    case "bad_request":
      return {
        title: "Invalid request",
        hint: "One or more fields didn't pass validation — see details below.",
        detail,
      };
    case "unauthorized":
      return {
        title: "Not signed in",
        hint: "Your session may have expired. Sign in again.",
        detail,
      };
    default:
      return {
        title: err.code || "Send failed",
        hint: "See details below.",
        detail,
        messageId: err.messageId,
        logsUrl: err.logsUrl,
      };
  }
}

function parseRecipients(value: string): string[] {
  return value
    .split(/[,\n;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function coerceVariable(
  type: VariableType,
  raw: string | boolean,
): string | number | boolean | null {
  if (type === "boolean") return Boolean(raw);
  const text = typeof raw === "string" ? raw.trim() : String(raw);
  if (text === "") return null;
  if (type === "number") {
    const n = Number(text);
    return Number.isFinite(n) ? n : null;
  }
  return text;
}

export function SendPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState<Mode>("template");
  const [toInput, setToInput] = useState("");
  const [domain, setDomain] = useState("");
  const [localPart, setLocalPart] = useState("");
  const [replyTo, setReplyTo] = useState("");

  const [templateId, setTemplateId] = useState("");
  const [variables, setVariables] = useState<Record<string, string | boolean>>({});

  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [text, setText] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<SendError | null>(null);
  const [result, setResult] = useState<SendResult | null>(null);

  function fail(title: string, hint = "") {
    setError({ title, hint, detail: "" });
  }

  useEffect(() => {
    let alive = true;
    Promise.all([templatesApi.list(), providersApi.list()])
      .then(([t, p]) => {
        if (!alive) return;
        setTemplates(t.templates);
        setProviders(p.providers);
        const first = p.providers[0];
        if (first) setDomain(first.domain);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError({
          title: "Failed to load form data",
          hint: err instanceof Error ? err.message : "",
          detail: "",
        });
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.templateId === templateId) ?? null,
    [templates, templateId],
  );

  useEffect(() => {
    if (!selectedTemplate) {
      setVariables({});
      return;
    }
    const next: Record<string, string | boolean> = {};
    for (const [name, type] of Object.entries(selectedTemplate.variableSchema)) {
      next[name] = type === "boolean" ? false : "";
    }
    setVariables(next);
  }, [selectedTemplate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);

    const recipients = parseRecipients(toInput);
    if (recipients.length === 0) {
      fail("Enter at least one recipient.");
      return;
    }
    if (!localPart || !domain) {
      fail("From address is required.");
      return;
    }

    const from = `${localPart.trim()}@${domain}`;

    let payloadExtras: Record<string, unknown> = {};
    if (mode === "template") {
      if (!selectedTemplate) {
        fail("Select a template.");
        return;
      }
      const coerced: Record<string, string | number | boolean> = {};
      for (const [name, type] of Object.entries(selectedTemplate.variableSchema)) {
        const v = coerceVariable(type, variables[name] ?? "");
        if (v === null) {
          fail(`Variable "${name}" is required (${type}).`);
          return;
        }
        coerced[name] = v;
      }
      payloadExtras = { templateId: selectedTemplate.templateId, variables: coerced };
    } else {
      if (!subject) {
        fail("Subject is required for raw sends.");
        return;
      }
      if (!html && !text) {
        fail("Provide HTML body or plain text body.");
        return;
      }
      payloadExtras = { subject, ...(html ? { html } : {}), ...(text ? { text } : {}) };
    }

    setSubmitting(true);
    try {
      const to: string | string[] =
        recipients.length === 1 ? (recipients[0] as string) : recipients;
      const res = await sendApi.send({
        to,
        from,
        ...(replyTo ? { replyTo } : {}),
        ...payloadExtras,
      });
      setResult(res);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(explainError(err));
      } else {
        fail("Send failed", err instanceof Error ? err.message : "");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const noProviders = providers.length === 0;
  const noTemplates = templates.length === 0;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Send email</Typography>
      </Stack>

      {noProviders && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No verified domains. Add one on the Providers page before sending.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>{error.title}</AlertTitle>
          {error.hint && <Box sx={{ mb: error.detail ? 1 : 0 }}>{error.hint}</Box>}
          {error.detail && (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                bgcolor: "rgba(0,0,0,0.04)",
                borderRadius: 1,
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {error.detail}
            </Box>
          )}
          {(error.messageId || error.logsUrl) && (
            <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
              {error.messageId && (
                <Typography variant="caption">
                  messageId <code>{error.messageId}</code>
                </Typography>
              )}
              {error.logsUrl && (
                <Link href={error.logsUrl} target="_blank" rel="noopener" variant="caption">
                  View CloudWatch logs ↗
                </Link>
              )}
            </Box>
          )}
        </Alert>
      )}

      {result && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <AlertTitle>Sent via {result.provider}</AlertTitle>
          <Box>
            messageId <code>{result.messageId}</code>
            {result.providerMessageId ? (
              <>
                {" "}
                · provider id <code>{result.providerMessageId}</code>
              </>
            ) : null}
          </Box>
          {result.logsUrl && (
            <Box sx={{ mt: 1 }}>
              <Link href={result.logsUrl} target="_blank" rel="noopener" variant="caption">
                View CloudWatch logs ↗
              </Link>
            </Box>
          )}
        </Alert>
      )}

      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Envelope
            </Typography>
            <Stack spacing={2} mt={1}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="From — local part"
                  value={localPart}
                  onChange={(e) => setLocalPart(e.target.value)}
                  required
                  fullWidth
                  disabled={noProviders}
                  placeholder="hello"
                />
                <FormControl sx={{ minWidth: 220 }} disabled={noProviders}>
                  <InputLabel>From — domain</InputLabel>
                  <Select
                    label="From — domain"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  >
                    {providers.map((p) => (
                      <MenuItem key={p.domain} value={p.domain}>
                        @{p.domain}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <TextField
                label="To"
                value={toInput}
                onChange={(e) => setToInput(e.target.value)}
                required
                fullWidth
                multiline
                minRows={1}
                helperText="One or more recipients, separated by comma or newline (max 50)."
              />
              <TextField
                label="Reply-to (optional)"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                fullWidth
                type="email"
              />
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Tabs value={mode} onChange={(_, v) => setMode(v as Mode)}>
              <Tab value="template" label="Template" />
              <Tab value="raw" label="Raw" />
            </Tabs>
            <Divider sx={{ mb: 3 }} />

            {mode === "template" ? (
              <Stack spacing={2}>
                {noTemplates ? (
                  <Alert severity="info">
                    No templates yet. Create one on the Templates page or switch to Raw.
                  </Alert>
                ) : (
                  <FormControl fullWidth required>
                    <InputLabel>Template</InputLabel>
                    <Select
                      label="Template"
                      value={templateId}
                      onChange={(e) => setTemplateId(e.target.value)}
                    >
                      {templates.map((t) => (
                        <MenuItem key={t.templateId} value={t.templateId}>
                          {t.name} <small style={{ marginLeft: 8, opacity: 0.6 }}>({t.templateId})</small>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {selectedTemplate && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Subject preview
                      </Typography>
                      <Typography variant="body2">{selectedTemplate.subject}</Typography>
                    </Box>

                    {Object.keys(selectedTemplate.variableSchema).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        This template has no variables.
                      </Typography>
                    ) : (
                      <Stack spacing={2}>
                        <Typography variant="overline" color="text.secondary">
                          Variables
                        </Typography>
                        {Object.entries(selectedTemplate.variableSchema).map(
                          ([name, type]) =>
                            type === "boolean" ? (
                              <FormControlLabel
                                key={name}
                                control={
                                  <Switch
                                    checked={Boolean(variables[name])}
                                    onChange={(e) =>
                                      setVariables((v) => ({ ...v, [name]: e.target.checked }))
                                    }
                                  />
                                }
                                label={`${name} (${type})`}
                              />
                            ) : (
                              <TextField
                                key={name}
                                label={name}
                                type={type === "number" ? "number" : "text"}
                                value={variables[name] ?? ""}
                                onChange={(e) =>
                                  setVariables((v) => ({ ...v, [name]: e.target.value }))
                                }
                                fullWidth
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Typography variant="caption" color="text.secondary">
                                        {type}
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            ),
                        )}
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <TextField
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  label="HTML body"
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  fullWidth
                  multiline
                  minRows={6}
                  placeholder="<p>Hello…</p>"
                />
                <TextField
                  label="Plain text body (optional)"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
                <Typography variant="caption" color="text.secondary">
                  Provide an HTML body, plain text body, or both.
                </Typography>
              </Stack>
            )}
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={
              submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />
            }
            disabled={submitting || noProviders}
          >
            {submitting ? "Sending…" : "Send"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
